import { Loader2 } from 'lucide-react';

export function ActionButton({
  icon: Icon,
  label,
  loading,
  onClick,
}: {
  icon: React.ElementType;
  label: string;
  loading: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      title={label}
      className="p-2 rounded-lg border border-gray-200 dark:border-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-950 disabled:opacity-50 transition-colors"
    >
      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />}
    </button>
  );
}

export function QuickActionButton({
  icon: Icon,
  label,
  loading,
  onClick,
  variant = 'default',
}: {
  icon: React.ElementType;
  label: string;
  loading: boolean;
  onClick: () => void;
  variant?: 'default' | 'warning' | 'success' | 'danger';
}) {
  const variants: Record<string, string> = {
    default: 'border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-950',
    warning: 'border-amber-200 dark:border-amber-900/50 text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950/40',
    success: 'border-green-200 dark:border-green-900/50 text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-950/40',
    danger: 'border-red-200 dark:border-red-900/50 text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40',
  };

  return (
    <button
      onClick={onClick}
      disabled={loading}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors disabled:opacity-50 ${variants[variant]}`}
    >
      {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />}
      {label}
    </button>
  );
}

export function StatCard({
  label,
  value,
  icon: Icon,
  accent,
}: {
  label: string;
  value: string;
  icon: React.ElementType;
  accent?: 'red' | 'blue';
}) {
  const colorMap = { red: 'text-red-600 dark:text-red-400', blue: 'text-blue-600 dark:text-blue-400' };
  const iconColorMap = { red: 'text-red-400', blue: 'text-blue-400' };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-3">
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className={`w-3.5 h-3.5 ${accent ? iconColorMap[accent] : 'text-gray-400'}`} />
        <span className="text-xs text-gray-400 dark:text-gray-500">{label}</span>
      </div>
      <div className={`text-lg font-semibold truncate ${accent ? colorMap[accent] : 'text-gray-900'}`}>
        {value}
      </div>
    </div>
  );
}
